import { useMemo, useState } from "react";
import { parseProductIds, unwrapPayload } from "../../utilities";
import { Alert, Button, FormGroup, Input, Label } from "reactstrap";
import { ExportStatesTable } from "./ExportStatesTable";
import { ProductInputPreview } from "./ProductInputPreview";

export function ProductStateSection
(
    {
        settings,
        prepareGoogleMerchantProduct,
        markProductsDirty,
        searchExportStates
    }
    :
    {
        settings: any | null;
        prepareGoogleMerchantProduct: (values: any) => Promise<any>;
        markProductsDirty: (values: any) => Promise<any>;
        searchExportStates: (values: any) => Promise<any>;
    }
)
{
    const [productIdsText, setProductIdsText] = useState('');
    const [stateResult, setStateResult] = useState<any | null>(null);
    const [productInputItems, setProductInputItems] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isRunning, setIsRunning] = useState(false);

    const activeProfiles = useMemo
    (
        () => (settings?.exportProfiles || []).filter((profile: any) => profile.enabled),
        [settings]
    );

    const activeProfileIds = activeProfiles
        .map((profile: any) => profile.profileId)
        .filter(Boolean);

    const getProductIds = () => parseProductIds(productIdsText);

    const validate = () =>
    {
        const productIds = getProductIds();

        if (productIds.length === 0)
        {
            setError('Podaj przynajmniej jeden ID produktu.');
            return null;
        }

        if (activeProfiles.length === 0)
        {
            setError('Brak aktywnych profili eksportu. Włącz przynajmniej jeden profil w konfiguracji.');
            return null;
        }

        return productIds;
    };

    const runSearchState = async () =>
    {
        setError(null);
        setIsRunning(true);

        const productIds = validate();

        if (!productIds)
        {
            setIsRunning(false);
            return;
        }

        try
        {
            const response = await searchExportStates({
                productIds,
                profileIds: activeProfileIds,
                limit: Math.max(100, productIds.length * activeProfileIds.length)
            });

            setStateResult(unwrapPayload(response));
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Pobieranie stanu produktów zakończyło się błędem.');
        }
        finally
        {
            setIsRunning(false);
        }
    };

    const runMarkDirty = async () =>
    {
        setError(null);
        setIsRunning(true);

        const productIds = validate();

        if (!productIds)
        {
            setIsRunning(false);
            return;
        }

        try
        {
            await markProductsDirty({
                productIds,
                profileIds: activeProfileIds,
                reason: 'Manual product diagnostics'
            });

            await runSearchState();
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Oznaczanie produktów jako dirty zakończyło się błędem.');
        }
        finally
        {
            setIsRunning(false);
        }
    };

    const runProductInputPreview = async () =>
    {
        setError(null);
        setProductInputItems([]);
        setIsRunning(true);

        const productIds = validate();

        if (!productIds)
        {
            setIsRunning(false);
            return;
        }

        try
        {
            const items = await Promise.all(
                productIds.flatMap(productId =>
                    activeProfiles.map(async (profile: any) =>
                    {
                        try
                        {
                            const response = await prepareGoogleMerchantProduct({
                                productId,
                                profileId: profile.profileId
                            });

                            return {
                                productId,
                                profileId: profile.profileId,
                                profileName: profile.name,
                                response: unwrapPayload(response)
                            };
                        }
                        catch (exception: any)
                        {
                            return {
                                productId,
                                profileId: profile.profileId,
                                profileName: profile.name,
                                error: exception?.message ?? 'Przygotowanie ProductInput zakończyło się błędem.'
                            };
                        }
                    })
                )
            );

            setProductInputItems(items);
        }
        finally
        {
            setIsRunning(false);
        }
    };

    return (
        <section>
            <h5>Stan produktów</h5>

            {activeProfiles.length > 0 ? (
                <Alert color="light">
                    <strong>Aktywne profile:</strong>
                    {' '}
                    {activeProfiles.map((profile: any) => (
                        <span key={profile.profileId} className="mr-3">
                            <code>{profile.profileId}</code>
                            {profile.name ? ` — ${profile.name}` : ''}
                        </span>
                    ))}
                </Alert>
            ) : (
                <Alert color="warning">
                    Brak aktywnych profili. Włącz profil w konfiguracji, aby diagnozować produkty.
                </Alert>
            )}

            {error && <Alert color="danger">{error}</Alert>}

            <FormGroup>
                <Label>ID produktów</Label>
                <Input
                    type="textarea"
                    rows={3}
                    value={productIdsText}
                    onChange={event => setProductIdsText(event.target.value)}
                    placeholder="Np. 1001, 1002, 1003"
                />
            </FormGroup>

            <div className="d-flex flex-wrap justify-content-end">
                <Button
                    type="button"
                    color="secondary"
                    className="mr-2 mb-2"
                    disabled={isRunning || activeProfiles.length === 0}
                    onClick={runSearchState}
                >
                    Pobierz stan
                </Button>

                <Button
                    type="button"
                    color="secondary"
                    className="mr-2 mb-2"
                    disabled={isRunning || activeProfiles.length === 0}
                    onClick={runProductInputPreview}
                >
                    Pokaż aktualny ProductInput
                </Button>

                <Button
                    type="button"
                    color="warning"
                    className="mb-2"
                    disabled={isRunning || activeProfiles.length === 0}
                    onClick={runMarkDirty}
                >
                    Oznacz jako dirty
                </Button>
            </div>

            {stateResult && (
                <div className="mt-4">
                    <h5>Stan zapisany w tabeli eksportu</h5>

                    {stateResult.message && (
                        <Alert color={stateResult.success === false ? 'warning' : 'success'}>
                            {stateResult.message}
                        </Alert>
                    )}

                    <ExportStatesTable states={stateResult.states || []} />

                    <details className="mt-4">
                        <summary>Raw JSON</summary>
                        <pre className="mt-3">{JSON.stringify(stateResult, null, 2)}</pre>
                    </details>
                </div>
            )}

            <ProductInputPreview items={productInputItems} />
        </section>
    );
}